import type { OnErrorFn } from '@formatjs/intl';
import { operationalEvent } from '../analytics/events';
import { intlErrorMatch } from '../intl-error-match';

type ProvidedI18nMessages = Record<string, string>;

export default function getMessages(): ProvidedI18nMessages {
	window.__JSD_I18N_MESSAGES = window.__JSD_I18N_MESSAGES || {};
	return window.__JSD_I18N_MESSAGES;
}

/**
 * Shared react-intl error reporter used by the top-level `IntlProvider` in
 * `src/providers.tsx` and by nested `IntlProvider`s (e.g. `PortalIntlBoundary`).
 *
 * Lives here rather than in `src/providers.tsx` so that nested-provider
 * consumers inside `src/view/common/*` can import it without pulling in
 * `providers.tsx` (which itself imports from `src/view/common/*` and would risk
 * a circular graph).
 *
 * Behaviour is preserved verbatim from the original in `src/providers.tsx`:
 *   - Missing-translation errors coming from external Fabric packages are ignored.
 *   - Missing-translation errors for locales we discontinued support for in 2018
 *     (`ro`, `pt`, `no`, `sk`, `et`, `is`) are ignored.
 *   - Everything else is reported as an `intl_translation missing` operational event.
 */
export const onIntlError = (intlError: string): void => {
	try {
		if (!intlError) return;
		const isExternalTranslation = intlError.match(
			/Missing message: "fabric\.(editor|media|theme|emoji)\..*/,
		);

		const { errorKey, errorLocale, isNonFinal } = intlErrorMatch(intlError);

		// According to https://hello.atlassian.net/wiki/spaces/i18n/pages/647802766/
		// Localized+Language+Support+Coverage+and+Strategy the following locales were
		// discontinued in 2018.
		const isUnSupportedLocale: boolean = !!intlError.match(
			/.*Missing message.*for locale "(ro|pt|no|sk|et|is)".*/,
		);

		if (isExternalTranslation || isUnSupportedLocale) {
			return;
		}

		operationalEvent({
			attributes: {
				isNonFinal,
				key: errorKey,
				locale: errorLocale,
			},
			actionSubject: 'intl_translation',
			action: 'missing',
			source: 'unknownSource',
		});
	} catch (err) {
		// oxlint-disable-next-line no-console
		console.error(`Failed to report intl error: ${intlError}`, err);
	}
};

export const onIntlNextError: OnErrorFn = (intlError) => {
	onIntlError(`IntlV5: ${intlError.message}`);
};
